import { Formik, FormikHelpers } from 'formik'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { MdCheck } from 'react-icons/md'

import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { ModalType, useModal } from 'src/providers/ModalProvider'
import { NotificationType, notify } from 'src/providers/NotificationProvider'
import { useUser } from 'src/providers/UserProvider'

import { PlanterRegistrationForm } from 'src/components/organisms/forms/PlanterRegistrationForm'
import { createAndDeleteAssets } from 'src/components/organisms/forms/PlanterRegistrationForm/helper'
import { PlanterRegistrationValues } from 'src/components/organisms/forms/PlanterRegistrationForm/types'
import { validationSchema } from 'src/components/organisms/forms/PlanterRegistrationForm/validationSchema'

import {
  CreatePlanterRegistrationMutation,
  namedOperations,
  UpdatePlanterRegistrationMutation,
  useCreatePlanterRegistrationAssetMutation,
  useCreatePlanterRegistrationMutation,
  useDeletePlanterRegistrationAssetMutation,
  useUpdatePlanterRegistrationMutation
} from 'src/config/generated/graphql'
import {
  adminAccountPlanterRegistrationPath,
  createPlanterRegistrationSuccessPath,
  dashboardPath,
  viewAccountPlanterRegistrationPath
} from 'src/config/paths'
import { S3Asset } from 'src/types/form'
import { PlanterRegistrationType } from 'src/types/planting'

export type PlanterRegistrationProps = {
  isAdmin?: boolean
  planterRegistration?: PlanterRegistrationType
  planterRegistrationPhotos?: S3Asset[]
  planterRegistrationDocuments?: S3Asset[]
}

export const PlanterRegistration = ({
  isAdmin,
  planterRegistration,
  planterRegistrationPhotos = [],
  planterRegistrationDocuments = []
}: PlanterRegistrationProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const { user } = useUser()
  const { openModal } = useModal()
  const { setFocussedHeader } = useFocusActionState()

  const [createPlanterRegistration] = useCreatePlanterRegistrationMutation()
  const [updatePlanterRegistration] = useUpdatePlanterRegistrationMutation({
    refetchQueries: [
      namedOperations.Query.getPlanterRegistration,
      namedOperations.Query.getApplication
    ]
  })

  const [createPlanterRegistrationAsset] =
    useCreatePlanterRegistrationAssetMutation({
      refetchQueries: [
        namedOperations.Query.getPlanterRegistration,
        namedOperations.Query.getApplication
      ]
    })
  const [deletePlanterRegistrationAsset] =
    useDeletePlanterRegistrationAssetMutation({
      refetchQueries: [
        namedOperations.Query.getPlanterRegistration,
        namedOperations.Query.getApplication
      ]
    })

  const planterRegistrationAssets = planterRegistrationPhotos.concat(
    planterRegistrationDocuments
  )

  const initialValues: PlanterRegistrationValues = {
    Description: planterRegistration?.Description || '',
    MonitoringDescription: planterRegistration?.MonitoringDescription || '',
    NumberOfTrees: planterRegistration?.NumberOfTrees || '500 to 5,000',
    PlantingAssets: planterRegistrationPhotos,
    PlantingDocuments: planterRegistrationDocuments,
    WorkflowState: planterRegistration?.WorkflowState || ''
  }

  const handleSubmit = useCallback(
    async (
      values: PlanterRegistrationValues,
      actions?: FormikHelpers<PlanterRegistrationValues>,
      isDraft: boolean = false
    ) => {
      setIsSubmitting(true)

      const { PlantingDocuments, PlantingAssets, ...rest } = values
      if (planterRegistration) {
        await updatePlanterRegistration({
          variables: {
            id: planterRegistration.Id,
            input: { ...rest, WorkflowState: isDraft ? 'Draft' : 'Submitted' }
          },
          onCompleted: async (response: UpdatePlanterRegistrationMutation) => {
            await createAndDeleteAssets({
              id: response.update_PlanterRegistration_by_pk?.Id,
              existingAssets: planterRegistrationAssets,
              onCreatePlanterRegistrationAsset: createPlanterRegistrationAsset,
              onDeletePlanterRegistrationAsset: deletePlanterRegistrationAsset,
              values
            })

            if (!isDraft) {
              notify({
                icon: MdCheck,
                message: `Thank you for adding your planting experience. You can now add your first planting project.`,
                type: NotificationType.success
              })
            }

            const accountId =
              response?.update_PlanterRegistration_by_pk?.Planter?.Account?.Id

            router.push(
              isAdmin
                ? adminAccountPlanterRegistrationPath({
                    routeParams: { accountId }
                  })
                : viewAccountPlanterRegistrationPath
            )
          }
        })
      } else {
        await createPlanterRegistration({
          variables: {
            input: {
              ...rest,
              PlanterId: user?.Account?.Planter?.Id,
              WorkflowState: isDraft ? 'Draft' : 'Submitted'
            }
          },
          onCompleted: async (response: CreatePlanterRegistrationMutation) => {
            await createAndDeleteAssets({
              id: response.create_planter_registration?.Id,
              existingAssets: planterRegistrationAssets,
              onCreatePlanterRegistrationAsset: createPlanterRegistrationAsset,
              onDeletePlanterRegistrationAsset: deletePlanterRegistrationAsset,
              values
            })

            if (!isDraft) {
              router.push(createPlanterRegistrationSuccessPath)
              return
            }

            const accountId =
              response?.create_planter_registration?.PlanterRegistration
                ?.Planter?.Account?.Id

            router.push(
              isAdmin
                ? adminAccountPlanterRegistrationPath({
                    routeParams: { accountId }
                  })
                : dashboardPath()
            )
          }
        })
      }
      setIsSubmitting(false)
    },
    [
      createPlanterRegistration,
      createPlanterRegistrationAsset,
      deletePlanterRegistrationAsset,
      isAdmin,
      planterRegistration,
      planterRegistrationAssets,
      router,
      updatePlanterRegistration,
      user?.Account?.Planter?.Id
    ]
  )

  const handleSaveAsDraft = useCallback(
    async (values: PlanterRegistrationValues) => {
      await handleSubmit(values, undefined, true)

      notify({
        icon: MdCheck,
        message:
          'Your project experience was saved as draft. You can continue to edit and submit any time.',
        type: NotificationType.success
      })
    },
    [handleSubmit]
  )

  const handleCancel = useCallback(() => {
    openModal(ModalType.Cancel, {
      destination: dashboardPath()
    })
  }, [openModal])

  useEffect(() => {
    setFocussedHeader(handleCancel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFocussedHeader])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <PlanterRegistrationForm
        isSubmitting={isSubmitting}
        onSaveAsDraft={handleSaveAsDraft}
        onCancel={handleCancel}
      />
    </Formik>
  )
}
